import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REASONS = [
  "Speaking",
  "Consulting",
  "Press",
  "Charity sector help",
  "Just saying hi",
  "Other",
];

const inputCls =
  "bg-[rgba(14,11,13,0.55)] border-[rgba(244,236,224,0.18)] text-[#F4ECE0] placeholder:text-[rgba(244,236,224,0.5)] focus-visible:ring-2 focus-visible:ring-[rgba(244,236,224,0.55)] focus-visible:ring-offset-0";

export default function ContactDialog({ open, onOpenChange, defaultReason }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("Speaking");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open && defaultReason && REASONS.includes(defaultReason)) {
      setReason(defaultReason);
    }
  }, [open, defaultReason]);

  // Reset 'sent' view when dialog closes
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => setSent(false), 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  const canSubmit =
    !sending &&
    name.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(email) &&
    message.trim().length >= 10;

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        reason,
        message: message.trim(),
        website,
      });
      setSent(true);
      // Clear fields
      setName("");
      setEmail("");
      setMessage("");
      setReason("Speaking");
      toast.success("Sent. I'll get back to you.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Couldn't send right now. Try again, or DM me on LinkedIn.";
      toast.error(typeof detail === "string" ? detail : "Couldn't send.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0E0B0D] border border-[rgba(244,236,224,0.18)] text-[#F4ECE0] sm:max-w-[480px] rounded-3xl"
        data-testid="contact-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#F4ECE0]">
            {sent ? "Thanks — message sent." : "Drop me an email"}
          </DialogTitle>
          <DialogDescription className="text-[rgba(244,236,224,0.72)]">
            {sent
              ? "It'll land in my inbox. I'll reply when I can — usually within a few days."
              : "Goes straight to my inbox. I read everything, reply when I can."}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="flex justify-end pt-2">
            <Button
              onClick={() => onOpenChange(false)}
              className="rounded-full bg-[#F4ECE0] text-[#0E0B0D] hover:bg-[#F8F1E7]"
              data-testid="contact-close-button"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2">
            {/* Honeypot field, visually hidden */}
            <div
              style={{
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: 1,
                height: 1,
                overflow: "hidden",
              }}
              aria-hidden="true"
            >
              <label>
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </label>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="contact-name"
                className="text-[rgba(244,236,224,0.85)] text-sm"
              >
                Your name
              </Label>
              <Input
                id="contact-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex Curious"
                maxLength={120}
                required
                className={inputCls}
                data-testid="contact-name-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="contact-email"
                className="text-[rgba(244,236,224,0.85)] text-sm"
              >
                Email
              </Label>
              <Input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="alex@example.com"
                required
                className={inputCls}
                data-testid="contact-email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[rgba(244,236,224,0.85)] text-sm">
                What's this about?
              </Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger
                  className={inputCls}
                  data-testid="contact-reason-trigger"
                >
                  <SelectValue placeholder="Pick one" />
                </SelectTrigger>
                <SelectContent
                  className="bg-[#0E0B0D] text-[#F4ECE0] border border-[rgba(244,236,224,0.18)]"
                  data-testid="contact-reason-options"
                >
                  {REASONS.map((r) => (
                    <SelectItem
                      key={r}
                      value={r}
                      className="focus:bg-[rgba(244,236,224,0.10)] focus:text-[#F4ECE0]"
                    >
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="contact-message"
                className="text-[rgba(244,236,224,0.85)] text-sm"
              >
                Message
              </Label>
              <Textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell me what you're thinking. The more specific, the better the reply."
                rows={5}
                maxLength={4000}
                required
                className={`${inputCls} resize-none min-h-[120px]`}
                data-testid="contact-message-input"
              />
              <p className="text-[11px] text-[rgba(244,236,224,0.55)]">
                {message.length}/4000 — minimum 10 characters
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={sending}
                className="text-[rgba(244,236,224,0.85)] hover:text-[#F4ECE0] hover:bg-[rgba(244,236,224,0.08)]"
                data-testid="contact-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit}
                className="rounded-full bg-[#F4ECE0] text-[#0E0B0D] hover:bg-[#F8F1E7] disabled:opacity-50"
                data-testid="contact-submit-button"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Small icon-export helper for parent components that want a matching button.
export function ContactTriggerIcon(props) {
  return <Mail {...props} />;
}
